import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Col, Row } from 'react-bootstrap';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import SearchBox from 'components/general/SearchBox';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Footer from 'components/general/Footer';
import ReactPagination from 'components/general/ReactPagination';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { GET_DEALER, FETCH_DEALER_ENTITLEMENTS, FETCH_DEALER_BRANCHES } from 'actions/dealerEntitlement';

const fmtDate = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—');
const money = (v) => (_.isNil(v) ? '—' : `₹${Number(v).toFixed(2)}`);

const STATUS_OPTIONS = _.map(['pending', 'active', 'expired', 'cancelled'], (status) => ({
  label: _.startCase(status),
  value: status,
}));

const CLAIM_OPTIONS = [
  { label: 'Claimed', value: 'true' },
  { label: 'Unclaimed', value: 'false' },
];

const Field = ({ label, children }) => (
  <Col md={4} className="mb-2">
    <div className="ec__field-label">{label}</div>
    <div className="ec__field-value">{children}</div>
  </Col>
);

Field.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
};

const DealerDetail = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dealerId } = useParams();
  const { navigateTo } = React.useContext(NavContext);

  const [tab, setTab] = useState('enrolments');
  const [filters, setFilters] = useState({});

  const dealer = useSelector((state) => state.dealerEntitlement.dealerDetail);
  const branches = useSelector((state) => state.dealerEntitlement.branches);
  const enrolments = useSelector((state) => state.dealerEntitlement.results);
  const stats = useSelector((state) => state.dealerEntitlement.stats);
  const isLoader = useSelector((state) => state.dealerEntitlement.isLoading);
  const page = useSelector((state) => state.dealerEntitlement.page);
  const limit = useSelector((state) => state.dealerEntitlement.limit);
  const totalPages = useSelector((state) => state.dealerEntitlement.totalPages);
  const total = useSelector((state) => state.dealerEntitlement.total);

  const getEnrolments = useCallback(
    (data = {}) => {
      const payload = _.omitBy({ ...filters, ...data, dealer: dealerId }, (v) => v === '' || _.isNil(v));
      dispatch({ type: FETCH_DEALER_ENTITLEMENTS.REQUEST, payload });
    },
    [filters, dealerId]
  );

  useEffect(() => {
    dispatch({ type: GET_DEALER.REQUEST, payload: dealerId });
    getEnrolments();
  }, [dealerId]);

  // Branches are fetched the first time the tab is opened, not on page load.
  useEffect(() => {
    if (tab === 'branches') {
      dispatch({ type: FETCH_DEALER_BRANCHES.REQUEST, payload: dealerId });
    }
  }, [tab, dealerId]);

  const cards = [
    { key: 'total', label: t('energyCredits.total', 'Total'), value: _.get(stats, 'total', 0) },
    { key: 'active', label: t('energyCredits.active', 'Active'), value: _.get(stats, 'active', 0) },
    { key: 'pending', label: t('energyCredits.pending', 'Pending'), value: _.get(stats, 'pending', 0) },
    { key: 'unclaimed', label: t('energyCredits.unclaimed', 'Unclaimed'), value: _.get(stats, 'unclaimed', 0) },
    { key: 'review', label: t('energyCredits.needsReview', 'Needs Review'), value: _.get(stats, 'needs_review', 0) },
    { key: 'city', label: t('energyCredits.unresolvedLocation', 'Unresolved Location'), value: _.get(stats, 'unresolved_location', 0) },
  ];

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={`${t('header.energyCredits', 'Energy Credits')} - ${_.get(dealer, 'name', '')}`} backTo="/energyCredits" />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
          <div className="page-content-wrapper scrollable">
            <div className="data-report--main energy-credits__main">
              <Card>
                <div className="tab-block ec__tabs">
                  <div className="tab-nav-block">
                    <a
                      href="#"
                      className={`tab-nav__item ${tab === 'enrolments' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setTab('enrolments');
                      }}
                    >
                      {t('energyCreditDealer.enrolments', 'Enrolments')}
                    </a>
                    <a
                      href="#"
                      className={`tab-nav__item ${tab === 'config' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setTab('config');
                      }}
                    >
                      {t('energyCreditDealer.config', 'Configuration')}
                    </a>
                    <a
                      href="#"
                      className={`tab-nav__item ${tab === 'branches' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setTab('branches');
                      }}
                    >
                      {t('energyCreditDealer.branches', 'Branches')}
                    </a>
                  </div>
                </div>
              </Card>

              {tab === 'config' && (
                <Card>
                  <div className="data-report__inner">
                    <Row>
                      <Field label={t('energyCreditDealer.name', 'Dealer')}>{_.get(dealer, 'name', '—')}</Field>
                      {/* code ties this dealer to the api key in the server env */}
                      <Field label={t('energyCreditDealer.code', 'Code')}>{_.get(dealer, 'code', '—')}</Field>
                      <Field label={t('energyCreditDealer.status', 'Status')}>
                        {_.get(dealer, 'is_active') ? t('energyCreditDealer.active', 'Active') : t('energyCreditDealer.paused', 'Paused')}
                      </Field>
                      <Field label={t('energyCreditDealer.totalAmount', 'Total Amount')}>{money(_.get(dealer, 'program.total_amount'))}</Field>
                      <Field label={t('energyCreditDealer.percent', 'Redemption %')}>{`${_.get(dealer, 'program.redemption_percent', 0)}%`}</Field>
                      <Field label={t('energyCreditDealer.minSession', 'Min Session Amount')}>
                        {money(_.get(dealer, 'program.min_session_amount'))}
                      </Field>
                      <Field label={t('energyCreditDealer.maxSession', 'Max Per Session')}>
                        {/* null is no cap at all, which zero would not convey */}
                        {_.isNil(_.get(dealer, 'program.max_redemption_per_session'))
                          ? t('energyCreditDealer.noCap', 'No cap')
                          : money(_.get(dealer, 'program.max_redemption_per_session'))}
                      </Field>
                      <Field label={t('energyCreditDealer.carryForward', 'Carry Forward')}>
                        {_.get(dealer, 'program.carry_forward') === false
                          ? t('energyCreditDealer.carryOff', 'No — each year expires at its own year end')
                          : t('energyCreditDealer.carryOn', 'Yes — unused credit accumulates to the programme end')}
                      </Field>
                      <Field label={t('energyCreditDealer.external', 'External Stations')}>
                        {_.get(dealer, 'redemption_scope.include_external_station')
                          ? t('energyCreditDealer.externalOn', 'Included')
                          : t('energyCreditDealer.externalOff', 'Own stations only')}
                      </Field>
                    </Row>

                    <div className="ec__section-title mt-3 mb-2">{t('energyCreditDealer.schedule', 'Yearly Release')}</div>
                    <div className="table-responsive">
                      <table className="record-list-table">
                        <thead>
                          <tr>
                            <th>{t('energyCreditDealer.year', 'Year')}</th>
                            <th>{t('energyCreditDealer.amount', 'Amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {_.isEmpty(_.get(dealer, 'program.schedule')) ? (
                            <tr>
                              <td colSpan={2} className="border-0">
                                <div className="empty-data-block">{t('energyCreditDealer.noSchedule', 'No yearly release configured')}</div>
                              </td>
                            </tr>
                          ) : (
                            _.map(_.get(dealer, 'program.schedule'), (row, index) => (
                              <tr key={`schedule-${index}`}>
                                <td>{row.year}</td>
                                <td>{money(row.amount)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="ec__hint mt-2">
                      {t(
                        'energyCreditDealer.readOnly',
                        'Programme terms are read-only here. A car keeps the terms it was sold under, and the credit clock runs from its sale date.'
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {tab === 'enrolments' && (
                <React.Fragment>
                  <Card>
                    <div className="data-report__inner">
                      <div className="ec__stats">
                        {_.map(cards, (card) => (
                          <div className="ec__stat" key={card.key}>
                            <div className="ec__stat-label">{card.label}</div>
                            <div className="ec__stat-value">{isLoader ? <Skeleton height={15} width={50} /> : card.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>

                  <div className="user-group-main-block">
                    <Card>
                      <div className="user-group-block">
                        <div className="user-group-search-box">
                          <Row>
                            <Col lg={4} md={6}>
                              <SearchBox
                                value={_.get(filters, 'search', '')}
                                placeholder={t('placeholder.searchEnrollment', 'Search reg no / VIN / phone / name')}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                onKeyPress={(e) => e.key === 'Enter' && getEnrolments({ page: 1 })}
                              />
                            </Col>
                            <Col lg={2} md={6}>
                              <Select
                                options={STATUS_OPTIONS}
                                placeholder={t('filters.allStatuses', 'All statuses')}
                                value={_.get(filters, 'status', '')}
                                isClearable
                                // The list reads pending to cancelled, which alphabetical order would scramble.
                                disableSort
                                onChange={(val) => setFilters({ ...filters, status: val || '' })}
                              />
                            </Col>
                            <Col lg={2} md={6}>
                              <Select
                                options={CLAIM_OPTIONS}
                                placeholder={t('filters.allClaims', 'Claimed & unclaimed')}
                                value={_.get(filters, 'claimed', '')}
                                isClearable
                                disableSort
                                onChange={(val) => setFilters({ ...filters, claimed: val || '' })}
                              />
                            </Col>
                            <Col lg={4} md={12} className="ec__actions">
                              <Button className="user-group-btn" onClick={() => getEnrolments({ page: 1 })}>
                                {t('button.apply', 'Apply')}
                              </Button>
                              <Button
                                className="user-group-btn"
                                onClick={() => {
                                  setFilters({});
                                  dispatch({
                                    type: FETCH_DEALER_ENTITLEMENTS.REQUEST,
                                    payload: { dealer: dealerId },
                                  });
                                }}
                              >
                                {t('button.clear', 'Clear')}
                              </Button>
                            </Col>
                          </Row>
                        </div>

                        <div className="user-group-table">
                          <div className="table-responsive">
                            <table className="record-list-table">
                              <thead>
                                <tr>
                                  <th>{t('enrollmentList.rto', 'Registration No')}</th>
                                  <th>{t('enrollmentList.buyer', 'Buyer')}</th>
                                  <th>{t('enrollmentList.phone', 'Phone')}</th>
                                  <th>{t('enrollmentList.vehicle', 'Vehicle')}</th>
                                  <th>{t('enrollmentList.branch', 'Branch')}</th>
                                  <th>{t('enrollmentList.enrolled', 'Enrolled')}</th>
                                  <th>{t('enrollmentList.activated', 'Activated')}</th>
                                  <th>{t('enrollmentList.claimed', 'Claimed')}</th>
                                  <th>{t('enrollmentList.credits', 'Credits Used')}</th>
                                  <th>{t('enrollmentList.status', 'Status')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {isLoader ? (
                                  <tr>
                                    <td colSpan={10} className="border-0">
                                      <Skeleton height={15} />
                                    </td>
                                  </tr>
                                ) : _.isEmpty(enrolments) ? (
                                  <tr>
                                    <td colSpan={10} className="border-0">
                                      <div className="empty-data-block">{t('enrollmentList.noEnrollments', 'No enrolments found')}</div>
                                    </td>
                                  </tr>
                                ) : (
                                  _.map(enrolments, (item, index) => (
                                    <tr
                                      key={`enrolment-${index}`}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => navigateTo(`/energyCredits/${dealerId}/enrolments/${_.get(item, 'id')}`)}
                                    >
                                      <td>{_.get(item, 'rto_no', '—')}</td>
                                      <td>{_.get(item, 'buyer.name', '—')}</td>
                                      <td>{_.get(item, 'buyer.phone', '—')}</td>
                                      <td>
                                        {_.get(item, 'vehicle_details.make', '')} {_.get(item, 'vehicle_details.model', '')}
                                      </td>
                                      <td>{_.get(item, 'dealer_branch.name') || _.get(item, 'branch_name_raw', '—')}</td>
                                      <td>{fmtDate(_.get(item, 'enrolled_at'))}</td>
                                      <td>{fmtDate(_.get(item, 'activated_at'))}</td>
                                      <td>{_.get(item, 'claimed') ? 'Yes' : 'No'}</td>
                                      {/* null means no ledger yet; zero would read as "spent nothing" */}
                                      <td>{_.isNil(_.get(item, 'credits')) ? '—' : money(_.get(item, 'credits.used'))}</td>
                                      <td>{_.startCase(_.get(item, 'status', ''))}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {!isLoader && !_.isEmpty(enrolments) && (
                          <ReactPagination
                            currentPage={page}
                            limit={limit}
                            total={total}
                            totalPages={totalPages}
                            handlePageClick={(pageVal) => getEnrolments({ page: pageVal.selected + 1 })}
                            marginPagesDisplayed={1}
                            showDataCount={true}
                          />
                        )}
                      </div>
                    </Card>
                  </div>
                </React.Fragment>
              )}

              {tab === 'branches' && (
                <div className="user-group-main-block">
                  <Card>
                    <div className="user-group-block">
                      <div className="user-group-table">
                        <div className="table-responsive">
                          <table className="record-list-table">
                            <thead>
                              <tr>
                                <th>{t('branchList.name', 'Branch')}</th>
                                <th>{t('branchList.city', 'City')}</th>
                                <th>{t('branchList.state', 'State')}</th>
                                <th>{t('branchList.poc', 'Contact Person')}</th>
                                <th>{t('branchList.enrolments', 'Enrolments')}</th>
                                <th>{t('branchList.active', 'Active')}</th>
                                <th>{t('branchList.pending', 'Pending')}</th>
                                <th>{t('branchList.firstSale', 'First Sale')}</th>
                                <th>{t('branchList.lastSale', 'Last Sale')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isLoader ? (
                                <tr>
                                  <td colSpan={9} className="border-0">
                                    <Skeleton height={15} />
                                  </td>
                                </tr>
                              ) : _.isEmpty(branches) ? (
                                <tr>
                                  <td colSpan={9} className="border-0">
                                    <div className="empty-data-block">
                                      {t('branchList.noBranches', "No branches yet — one is created on the dealer's first sale")}
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                _.map(branches, (item, index) => (
                                  <tr key={`branch-${index}`}>
                                    <td>
                                      {_.get(item, 'name', '—')}
                                      {/* A merged duplicate is kept for history; its sales already point at the real branch. */}
                                      {_.get(item, 'merged_into') && <span className="ec__unresolved">merged</span>}
                                      {/* Every other spelling the dealer has sent for this branch. */}
                                      {_.size(_.get(item, 'raw_names')) > 1 && (
                                        <div className="ec__hint">{_.without(_.get(item, 'raw_names'), _.get(item, 'name')).join(', ')}</div>
                                      )}
                                    </td>
                                    <td>
                                      {_.get(item, 'city') || '—'}
                                      {_.get(item, 'city') && !_.get(item, 'city_resolved') && <span className="ec__unresolved">unmatched</span>}
                                    </td>
                                    <td>
                                      {_.get(item, 'state') || '—'}
                                      {_.get(item, 'state') && !_.get(item, 'state_resolved') && <span className="ec__unresolved">unmatched</span>}
                                    </td>
                                    <td>
                                      {_.get(item, 'poc_name', '—')}
                                      {_.get(item, 'poc_contact') && <div className="ec__hint">{_.get(item, 'poc_contact')}</div>}
                                    </td>
                                    <td>{_.get(item, 'enrolments', 0)}</td>
                                    <td>{_.get(item, 'active', 0)}</td>
                                    <td>{_.get(item, 'pending', 0)}</td>
                                    <td>{fmtDate(_.get(item, 'first_seen_at'))}</td>
                                    <td>{fmtDate(_.get(item, 'last_sale_at'))}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="ec__hint mt-2">
                        {t('branchList.autoCreated', 'Branches are created from the location name the dealer sends with each sale.')}
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </SkeletonTheme>
        <Footer />
      </LayoutContainer>
    </React.Fragment>
  );
};

export default DealerDetail;
